import { Autocomplete, Divider, Radio, SelectChangeEvent } from "@mui/material";

import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Stack } from "@mui/material";

import { Paper } from "@mui/material";
import PRO_COLORS from "src/pages/proColors";
import ToggleVisitingPlayer from "./ToggleVisitingPlayer";
import { TeamPositionPlayers } from "./TeamSubmission";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useCallback, useEffect, useState } from "react";
import { CheckCircle } from "@mui/icons-material";
import PoloRating from "../PlayerManagement/PoloRating";
import { AvailablePlayer } from "./TeamSubmission";

export const USPARatingScale = [
  { value: -2, label: "-2" },
  { value: -1, label: "-1" },
  { value: 0, label: "0" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10" },
];

function TeamPosition({
  index,
  positionPlayers,
  teamMembers,
  setTeamMembers,
  availablePlayers,
  loadingPlayers,
  unavailablePlayers,
  isSinglePlayer,
  managerMode,
}: {
  index: number;
  positionPlayers: TeamPositionPlayers;
  teamMembers: TeamPositionPlayers[];
  setTeamMembers: (teamMembers: TeamPositionPlayers[]) => void;
  availablePlayers: AvailablePlayer[];
  loadingPlayers: boolean;
  unavailablePlayers: number[];
  isSinglePlayer: boolean;
  managerMode: number;
}) {
  const [isValidTeamPosition, setIsValidTeamPosition] = useState(false);

  // Get all selected player IDs from other positions
  const getSelectedPlayerIds = () => {
    const selectedIds = new Set<number>();
    unavailablePlayers.forEach(player => {
      selectedIds.add(player);
    });
    teamMembers.forEach((member, memberIndex) => {
      //if (memberIndex !== index) { // Skip current position
        member.players.forEach(player => {
          if (player.playerId) {
            selectedIds.add(player.playerId);
          }
        });
      //}
    });
    return selectedIds;
  };

  const handlePlayerChange = (
    index: number,
    positionPlayerIndex: number,
    event: SelectChangeEvent
  ) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index].players[positionPlayerIndex].playerId =
      parseInt(event.target.value);
    setTeamMembers(newTeamMembers);
  };

  const handleVisitingPlayerChange = (
    index: number,
    positionPlayerIndex: number,
    event: SelectChangeEvent
  ) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index].players[positionPlayerIndex].visitingPlayerName =
      event.target.value;
    setTeamMembers(newTeamMembers);
  };

  const handleSplitSpotChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index].isSplitSpot = event.target.checked;
    if (!event.target.checked && newTeamMembers[index].players.length > 1) {
      newTeamMembers[index].players.pop();
    }
    if (event.target.checked && newTeamMembers[index].players.length === 1 && !isSinglePlayer) {
      newTeamMembers[index].players.push({
        playerId: null,
        isVisitingPlayer: false,
        visitingPlayerName: "",
        visitingPlayerRating: 0,
      });
    }
    setTeamMembers(newTeamMembers);
  };

  const handleTeamCaptainChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers.forEach((member, memberIndex) => {
      member.isTeamCaptain = memberIndex === index;
      if(memberIndex === index && member.isSplitSpot) {
        member.isSplitSpot = false;
        member.players.pop();
      }
    });
    setTeamMembers(newTeamMembers);
  };
  const handlePlayerRatingChange = (
    index: number,
    positionPlayerIndex: number,
    newValue: number | number[]
  ) => {
    const newTeamMembers = [...teamMembers];
    newTeamMembers[index].players[positionPlayerIndex].visitingPlayerRating =
      newValue as number;
    setTeamMembers(newTeamMembers);
  };

  const validateTeamPosition = useCallback(() => {
    let isValid = true;
    positionPlayers.players.forEach((player) => {
      if(!player.playerId && !player.visitingPlayerName) {
        isValid = false;
      }
    });
    setIsValidTeamPosition(isValid);
    console.log(isValid);
  }, [positionPlayers])

  useEffect(() => {
    validateTeamPosition();
  }, [teamMembers, validateTeamPosition]);

  return (
    <Paper
      key={index}
      elevation={2}
      sx={{
        p: 2,
        mb: 3,
        borderLeft: `4px solid ${PRO_COLORS[index][1]}`,
        position: "relative",
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src={PRO_COLORS[index][1]}
              alt="Polo Pro Color"
              sx={{
                width: 30,
                height: 30,
                //display: { xs: "none", md: "block" },
              }}
            />
            <Typography variant="subtitle1" fontWeight="bold">
              Position {index + 1}
            </Typography>
            {!isSinglePlayer && (
              <Tooltip title="Team Captain. There should only be one team captain per team and they can't split a spot.">
                <FormControlLabel
                control={
                  <Radio
                    checked={positionPlayers.isTeamCaptain}
                    onChange={(e) => handleTeamCaptainChange(index, e)}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Team Captain
                  </Box>
                }
                />
              </Tooltip>
            )}
          </Stack>
          {isValidTeamPosition && (
            <CheckCircle color="success" sx={{ position: "absolute", right: 16, top: 16 }} />
          )}
        </Box>
        {positionPlayers.players.map((positionPlayer, positionPlayerIndex) => (
          <Stack spacing={2} key={positionPlayerIndex}>
            <ToggleVisitingPlayer
              positionPlayer={positionPlayer}
              index={index}
              positionIndex={positionPlayerIndex}
              teamMembers={teamMembers}
              setTeamMembers={setTeamMembers}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 8 }}>
                {!positionPlayer.isVisitingPlayer ? (
                   console.log(positionPlayer),
                    <Autocomplete
                      fullWidth
                      disabled={loadingPlayers || (isSinglePlayer && managerMode !== -1)}
                      options={availablePlayers}
                      value={availablePlayers.find(p => p.value === positionPlayer.playerId) || null}
                      onChange={(_, newValue) => {
                        handlePlayerChange(index, positionPlayerIndex, {
                          target: { value: newValue?.value || '' }
                        } as SelectChangeEvent)
                      }}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionDisabled={(option) => {
                        const selectedIds = getSelectedPlayerIds();
                        return selectedIds.has(option.value);
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ 
                          opacity: getSelectedPlayerIds().has(option.value) ? 0.5 : 1,
                          cursor: getSelectedPlayerIds().has(option.value) ? 'not-allowed' : 'default'
                        }}>
                          {option.label}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Player"
                          disabled={loadingPlayers}
                        />
                      )}
                      loading={loadingPlayers}
                    />
                    /*<Select
                      labelId={`player-select-label-${index}`}
                      value={positionPlayer.playerId}
                      label="Player"
                      onChange={(e) =>
                        handlePlayerChange(index, positionPlayerIndex, e)
                      }
                      disabled={loadingPlayers}
                    >
                      <MenuItem value="">
                        <em>Select a player</em>
                      </MenuItem>
                      {loadingPlayers ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                        </MenuItem>
                      ) : (
                        availablePlayers.map((player) => (
                          <MenuItem key={player.value} value={player.value}>
                            {player.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>*/
                ) : (
                  <TextField
                    fullWidth
                    label="Visiting Player Name"
                    value={positionPlayer.visitingPlayerName || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleVisitingPlayerChange(index, positionPlayerIndex, e)
                    }
                  />
                )}
              </Grid>
              {positionPlayerIndex === 0 && (
                <Grid size={{ xs: 4 }}>
                  <Tooltip title="Split spot allows two players to share one position">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={positionPlayers.isSplitSpot}
                          onChange={(e) => handleSplitSpotChange(index, e)}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          Split Spot
                          <HelpOutlineIcon sx={{ ml: 1, fontSize: 16 }} />
                        </Box>
                      }
                    />
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {positionPlayer.isVisitingPlayer && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Typography gutterBottom>Player Rating</Typography>
                  <PoloRating
                    playerRating={positionPlayer.visitingPlayerRating || 0}
                    setPlayerRating={(newValue) =>
                      handlePlayerRatingChange(
                        index,
                        positionPlayerIndex,
                        newValue as number
                      )
                    }
                    disabled={false}
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        ))}
        <Divider sx={{ my: 2 }} />
      </Stack>
    </Paper>
  );
}

export default TeamPosition;
